import type { Problem } from "./problem";

/**
 * Error hierarchy for the API layer. Messages on these errors are
 * developer-facing; user-visible text comes from mapping `problem.code`
 * (or `status`) to i18n keys at the UI layer.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly problem?: Problem;
  readonly requestId?: string;

  constructor(message: string, status: number, problem?: Problem, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.requestId = requestId;
  }

  /** Convenience accessor for the stable machine code, when the server sent one. */
  get code(): string | undefined {
    return this.problem?.code;
  }
}

/** 412 — optimistic-concurrency conflict (If-Match did not match current version). */
export class PreconditionFailedError extends ApiError {
  constructor(problem?: Problem, requestId?: string) {
    super(problem?.title ?? "Precondition failed", 412, problem, requestId);
    this.name = "PreconditionFailedError";
  }
}

/** 401 — no valid session. */
export class UnauthorizedError extends ApiError {
  constructor(problem?: Problem, requestId?: string) {
    super(problem?.title ?? "Unauthorized", 401, problem, requestId);
    this.name = "UnauthorizedError";
  }
}

/** Outcome-ambiguous transport failure, including an unreadable 2xx body. */
export class NetworkError extends Error {
  constructor(message = "Network request failed", options?: ErrorOptions) {
    super(message, options);
    this.name = "NetworkError";
  }
}
